import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  ArrowLeft, ChartPieSlice, ChartLineUp, LinkSimple, CaretRight, ArrowSquareOut,
} from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CampaignsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/campaigns");
      setCampaigns(data);
      if (data.length && !selected) setSelected(data[0].campaign);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [selected]);

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    api.get(`/analytics/campaign?name=${encodeURIComponent(selected)}&days=${range}`)
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail(null));
  }, [selected, range]);

  const chart = (detail?.timeseries || []).map((d) => ({ date: d.date.slice(5), clicks: d.clicks }));

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-40 bg-[#050505] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn"
              className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-white transition-colors"><ArrowLeft size={16} /></button>
            <div className="flex items-center gap-2"><ChartPieSlice size={18} weight="bold" /><span className="font-head font-extrabold tracking-tighter text-lg">CAMPAIGNS</span></div>
          </div>
          <span className="mono text-xs text-zinc-500 hidden sm:block">{user?.email}</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <p className="label-xs mb-2">// campaign overview</p>
        <h1 className="font-head text-3xl sm:text-4xl font-black tracking-tighter uppercase mb-8">Performance</h1>

        {loading && <p className="mono text-sm text-zinc-500">loading<span className="cursor-blink">_</span></p>}
        {!loading && campaigns.length === 0 && (
          <div className="border border-white/10 p-12 text-center" data-testid="empty-campaigns">
            <ChartPieSlice size={28} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No campaigns yet. Add a campaign label when you create a link.</p>
          </div>
        )}

        {campaigns.length > 0 && (
          <div className="grid lg:grid-cols-[280px_1fr] gap-8">
            {/* list */}
            <div className="space-y-2" data-testid="campaign-list">
              {campaigns.map((c) => (
                <button key={c.campaign} onClick={() => setSelected(c.campaign)} data-testid={`campaign-item-${c.campaign}`}
                  className={`w-full text-left border px-4 py-3 transition-colors ${selected === c.campaign ? "border-white bg-white/5" : "border-white/10 hover:border-white/30"}`}>
                  <div className="flex items-center justify-between">
                    <span className="mono text-sm text-white truncate">{c.campaign}</span>
                    <CaretRight size={14} className="text-zinc-600 shrink-0" />
                  </div>
                  <div className="mono text-[11px] text-zinc-500 mt-1">{c.links} link{c.links !== 1 ? "s" : ""} · {c.clicks} click{c.clicks !== 1 ? "s" : ""}</div>
                </button>
              ))}
            </div>

            {/* detail */}
            <div data-testid="campaign-detail">
              {!detail && <p className="mono text-sm text-zinc-500">loading<span className="cursor-blink">_</span></p>}
              {detail && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <h2 className="font-head text-2xl font-black tracking-tighter">{detail.campaign}</h2>
                    <div className="flex gap-px bg-white/10 border border-white/10">
                      {[7, 30, 90].map((r) => (
                        <button key={r} onClick={() => setRange(r)} data-testid={`camp-range-${r}`}
                          className={`px-4 h-9 mono text-xs uppercase tracking-wider transition-colors ${range === r ? "bg-white text-black" : "bg-[#0a0a0a] text-zinc-400 hover:text-white"}`}>{r}d</button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8">
                    <Metric label="Total clicks" value={detail.total_clicks} testid="camp-total-clicks" />
                    <Metric label={`Last ${range}d`} value={detail.range_clicks} testid="camp-range-clicks" />
                    <Metric label="Links" value={detail.total_links} testid="camp-links" />
                  </div>

                  <div className="flex items-center gap-2 label-xs mb-3"><ChartLineUp size={13} />// clicks over time</div>
                  <div className="border border-white/10 p-4 h-[220px] mb-8">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chart} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} interval="preserveStartEnd" />
                        <YAxis allowDecimals={false} tick={{ fill: "#52525b", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} contentStyle={{ background: "#000", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} itemStyle={{ color: "#fff" }} />
                        <Bar dataKey="clicks" fill="#ffffff" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex items-center gap-2 label-xs mb-3"><LinkSimple size={13} />// top links</div>
                  <div className="border border-white/10" data-testid="camp-top-links">
                    {detail.top_links.length === 0 && <p className="p-4 text-zinc-600 text-sm mono">no links</p>}
                    {detail.top_links.map((l) => (
                      <div key={l.code} className="flex items-center gap-3 px-4 py-3 border-b border-white/10 last:border-b-0">
                        <a href={l.short_url || `/r/${l.code}`} target="_blank" rel="noreferrer" className="mono text-sm text-white hover:underline shrink-0">/r/{l.code}</a>
                        <span className="mono text-xs text-zinc-500 truncate flex-1 flex items-center gap-1"><span className="truncate">{l.destination_url}</span><ArrowSquareOut size={11} className="text-zinc-600 shrink-0" /></span>
                        <span className="mono text-sm tabular-nums text-white shrink-0">{l.clicks}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="bg-[#0a0a0a] px-5 py-4" data-testid={testid}>
      <div className="label-xs mb-1">{label}</div>
      <div className="font-head text-2xl font-black tabular-nums">{value}</div>
    </div>
  );
}
